import { useEffect, useRef } from "react";
import { subscribeTradeExecuted } from "../../utils/tradeEvents";
import { ConvergenceRun, runUntilConverged } from "./tradeSync.runner";
import { RETRY_POLICY } from "./tradeSync.constants";
import { clearSyncStatus, setLive, setStale, setSyncing } from "./syncStatus";

interface UseTradeSyncTargetOptions<T> {
  /**
   * Identifies this target in the shared status store, so a view can ask whether
   * THIS list is confirmed current. Every convergence owner must supply one:
   * status used to be published by the paginated-history hook alone, which meant
   * the account-derived surfaces (PositionsTab, MobilePositionsTab, portfolio)
   * could fail to converge and say nothing at all — the exact "present an
   * unconfirmed answer as current" this layer exists to prevent.
   */
  targetId: string;
  /** Disabled until there is an account to sync for. */
  enabled: boolean;
  /** Latest value this owner holds, read at trade time to form the baseline. */
  current: () => T;
  /** One network read, resolving with the server's new answer. */
  refetch: () => Promise<T>;
  /** Reduce a payload to the fields a trade would move. */
  signature: (value: T) => string;
}

/**
 * Refetch this owner's data after a confirmed trade until the server's answer
 * actually changes, then stop.
 *
 * Each data owner runs its own convergence because only the owner has both a
 * bound mutate that resolves with the new payload and a signature meaningful
 * for its shape. That is what replaced the broad key-filter sweep, which fired
 * a fixed ladder across every trading endpoint whether or not anything changed
 * — and which could never reach the `$inf$` caches anyway.
 */
export function useTradeSyncTarget<T>({
  targetId,
  enabled,
  current,
  refetch,
  signature,
}: UseTradeSyncTargetOptions<T>): void {
  const currentRef = useRef(current);
  currentRef.current = current;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  const runRef = useRef<ConvergenceRun | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubscribe = subscribeTradeExecuted(() => {
      const before = signatureRef.current(currentRef.current());
      runRef.current?.cancel();
      setSyncing(targetId, 0, RETRY_POLICY.maxAttempts);
      const run = runUntilConverged<T>({
        refetch: () => refetchRef.current(),
        isConverged: (next) => signatureRef.current(next) !== before,
        onAttempt: (attempt) =>
          setSyncing(targetId, attempt, RETRY_POLICY.maxAttempts),
      });
      runRef.current = run;

      void run.done.then((outcome) => {
        // A superseded or unmounted run proved nothing; reporting LIVE would be
        // asserting a check that never completed.
        if (runRef.current !== run || outcome.status === "cancelled") return;
        if (outcome.status === "converged") setLive(targetId);
        else if (outcome.status === "errored") setStale(targetId, "NETWORK");
        else setStale(targetId, "NOT_CONVERGED");
      });
    });
    return () => {
      unsubscribe();
      runRef.current?.cancel();
      clearSyncStatus(targetId);
    };
  }, [enabled, targetId]);
}
